import { useMemo, useState } from 'react';

import { useTracker } from '@archipro-website/tracker';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { AwardProps } from '@modules/page-builder/component/detail/banner-awards/types';
import { LoadMoreButton } from './LoadMoreButton';
import { SECTION_TITLE_TYPE } from './SectionHead';

/** One award, as the detail pages list it. */
export interface DetailAward {
    id: string | number;
    /** Free text from Business Manager, usually a year. */
    year?: string | null;
    /** Business Manager's "Name of award". */
    title: string;
    /** Business Manager's "Organisation". */
    institution?: string | null;
    /**
     * The live project or product the award was for, where the list belongs
     * to someone other than it (a practice's awards). Absent on an item's own
     * page, where the award is for the page itself.
     */
    item?: {
        kind: 'Project' | 'Product';
        title: string;
        link: string;
        thumbnail?: string;
    };
}

export type DetailAwardsTheme = 'light' | 'dark';

const AWARDS_INITIAL = 8;
const AWARDS_STEP = 8;

const THEME: Record<
    DetailAwardsTheme,
    {
        rule: string;
        card: string;
        cardHover: string;
        year: string;
        value: string;
        label: string;
        muted: string;
        cta: string;
    }
> = {
    light: {
        rule: 'max-md:border-black/10',
        card: 'md:bg-white',
        cardHover: 'md:hover:bg-[#fafafa]',
        year: 'text-[#1f1f21]',
        value: 'text-[#141414]',
        label: 'text-[#767676]',
        muted: 'text-[#767676]',
        cta: 'border-[#181818] text-[#181818] group-hover/row:bg-[#181818] group-hover/row:text-white',
    },
    dark: {
        rule: 'max-md:border-white/10',
        card: 'md:bg-white/[0.06]',
        cardHover: 'md:hover:bg-white/[0.1]',
        year: 'text-white',
        value: 'text-white',
        label: 'text-white/50',
        muted: 'text-white/50',
        cta: 'border-white text-white group-hover/row:bg-white group-hover/row:text-[#181818]',
    },
};

/**
 * Newest first. `year` is free text, so anything that isn't a number sorts
 * last rather than poisoning the comparison; awards sharing a year keep their
 * Business Manager order (sort is stable).
 */
const byYearDesc = (a: DetailAward, b: DetailAward) => {
    const yearA = Number(a.year);
    const yearB = Number(b.year);
    const validA = a.year != null && Number.isFinite(yearA);
    const validB = b.year != null && Number.isFinite(yearB);
    if (!validA || !validB) return Number(validB) - Number(validA);
    return yearB - yearA;
};

/**
 * The awards list every detail page shares: a white card per award on
 * desktop, a hairline row on the phone. Fields follow Business Manager's
 * order — year, the award's name, then the organisation — with the project
 * or product it was for, and a way to it, where the list carries one.
 */
export const DetailAwardsList = ({
    awards,
    theme = 'light',
    className,
}: {
    awards: DetailAward[];
    theme?: DetailAwardsTheme;
    className?: string;
}) => {
    const [visible, setVisible] = useState(AWARDS_INITIAL);
    // The route does not remount between two pages of the same kind, so
    // without this a reader who opened one list's older awards would arrive
    // at the next with the same number already showing.
    const [shownFor, setShownFor] = useState(awards);
    if (shownFor !== awards) {
        setShownFor(awards);
        setVisible(AWARDS_INITIAL);
    }

    // Sorted before the slice, so "Show more" reveals older awards instead
    // of reshuffling the visible ones.
    const sorted = useMemo(() => [...awards].sort(byYearDesc), [awards]);
    const shown = sorted.slice(0, visible);
    // Only a list that names what each award was for needs the column.
    const hasItems = awards.some((award) => award.item);
    const tone = THEME[theme];

    return (
        <div
            className={cn(
                'flex flex-col max-md:border-t md:gap-[0.25rem]',
                tone.rule,
                className,
            )}
        >
            {shown.map((award) => (
                <AwardRow
                    key={award.id}
                    award={award}
                    hasItems={hasItems}
                    theme={theme}
                />
            ))}
            {visible < awards.length ? (
                <LoadMoreButton
                    tone={theme}
                    onClick={() => setVisible((value) => value + AWARDS_STEP)}
                    className="pt-[2.5rem]"
                />
            ) : null}
        </div>
    );
};

const AwardRow = ({
    award,
    hasItems,
    theme,
}: {
    award: DetailAward;
    hasItems: boolean;
    theme: DetailAwardsTheme;
}) => {
    const tone = THEME[theme];
    const { item } = award;
    const tracker = useTracker();
    // The legacy award badge's event for following an award to its work.
    const logAwardClick = (link: string) => {
        void tracker.log('AwardClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: { ExtraData: JSON.stringify({ link }) },
        });
    };
    const row = (
        <>
            {/* Below md: the year in Gestura, the award over its
                organisation, what it was for under them, on a hairline. */}
            <div className="flex gap-4 md:hidden">
                <span
                    className={cn(
                        'w-[4.25rem] shrink-0 font-gestura text-[1.75rem] leading-none',
                        tone.year,
                    )}
                >
                    {award.year}
                </span>
                <div className="min-w-0 flex-1 pt-[0.2rem]">
                    <p
                        className={cn(
                            'm-0 font-sans text-sm leading-[1.3] font-medium',
                            tone.value,
                        )}
                    >
                        {award.title}
                    </p>
                    {award.institution ? (
                        <p
                            className={cn(
                                'm-0 mt-0.5 font-sans text-sm leading-[1.3]',
                                tone.muted,
                            )}
                        >
                            {award.institution}
                        </p>
                    ) : null}
                    {item ? (
                        <p
                            className={cn(
                                'm-0 mt-1 font-sans text-xs leading-[1.3]',
                                tone.muted,
                            )}
                        >
                            {item.title}
                        </p>
                    ) : null}
                </div>
            </div>
            <span
                className={cn(
                    'font-sans leading-tight font-medium max-md:hidden md:text-[1.375rem]',
                    tone.year,
                )}
            >
                {award.year}
            </span>
            <AwardField label="Award" value={award.title} theme={theme} />
            <AwardField
                label="Organisation"
                value={award.institution}
                theme={theme}
            />
            {hasItems ? (
                <AwardField
                    label={item?.kind ?? 'Project'}
                    value={item?.title}
                    theme={theme}
                />
            ) : null}
            {hasItems ? <AwardCta item={item} theme={theme} /> : null}
        </>
    );

    // Desktop: the card row. Below md: the list row on its hairline.
    const rowClassName = cn(
        'group/row relative transition-colors max-md:border-b max-md:py-4 md:grid md:min-h-[6.25rem] md:items-center md:gap-x-[1.5rem] md:gap-y-3 md:rounded-tile-card md:px-[2.5rem]',
        hasItems
            ? 'md:grid-cols-[13%_1fr_22%_19%_auto]'
            : 'md:grid-cols-[13%_1fr_30%]',
        tone.rule,
        tone.card,
    );

    // A row whose award has no live item has nowhere to go, so it stays plain
    // markup rather than a dead link.
    return item ? (
        <RoccoLink
            to={item.link}
            onClick={() => logAwardClick(item.link)}
            className={cn(rowClassName, tone.cardHover)}
        >
            {row}
        </RoccoLink>
    ) : (
        <div className={rowClassName}>{row}</div>
    );
};

const AwardField = ({
    label,
    value,
    theme,
}: {
    label: string;
    value?: string | null;
    theme: DetailAwardsTheme;
}) =>
    value ? (
        <div className="flex min-w-0 flex-col gap-[0.375rem] max-md:hidden">
            <span
                className={cn(
                    'font-sans leading-none md:text-[0.875rem] md:tracking-[-0.026rem]',
                    THEME[theme].label,
                )}
            >
                {label}
            </span>
            <span
                className={cn(
                    'truncate font-sans leading-none font-medium md:text-[1.25rem] md:tracking-[-0.025rem]',
                    THEME[theme].value,
                )}
            >
                {value}
            </span>
        </div>
    ) : (
        <span />
    );

/**
 * View Project / View Product. A span, not a link: the whole row is already
 * the link, and a link inside a link is neither valid nor reachable.
 * `group/cta` is what the preview opens on, so it answers to this control
 * alone rather than to anywhere on the row.
 */
const AwardCta = ({
    item,
    theme,
}: {
    item: DetailAward['item'];
    theme: DetailAwardsTheme;
}) =>
    item ? (
        <span
            className={cn(
                'group/cta relative inline-flex h-10 items-center justify-center rounded-[var(--radius-btn-m)] border-[1.347px] px-[1.18rem] font-sans text-base font-medium whitespace-nowrap transition-colors max-md:hidden',
                THEME[theme].cta,
            )}
        >
            {item.kind === 'Product' ? 'View Product' : 'View Project'}
            {item.thumbnail ? (
                // To the control's left, centred on it, floating over the
                // rows either side; `pointer-events-none` so it can never
                // swallow the click it is previewing. Desktop only — there
                // is no hover to open it on touch.
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute top-1/2 right-[calc(100%+0.75rem)] z-30 block w-[26.6rem] -translate-y-1/2 overflow-hidden rounded-tile opacity-0 shadow-[0_8px_28px_rgba(0,0,0,0.18)] transition-opacity duration-200 group-hover/cta:opacity-100 max-md:hidden"
                >
                    <img
                        src={item.thumbnail}
                        alt=""
                        loading="lazy"
                        className="block aspect-[425/289] w-full object-cover"
                    />
                </span>
            ) : null}
        </span>
    ) : (
        <span />
    );

/**
 * An item page's own awards — the product's or the project's — as the list
 * takes them. The award is for the page itself, so it names nothing else.
 */
export const toDetailAwards = (awards: AwardProps[]): DetailAward[] =>
    awards.map((award) => ({
        id: award.id,
        year: award.year,
        title: award.title,
        institution: award.institution,
    }));

/**
 * Awards as a section of their own on an item's page, headed in the page's
 * section type and standing open, as the profile's are, rather than folded
 * into an accordion row.
 */
export const DetailAwardsSection = ({
    awards,
    theme = 'light',
    className,
}: {
    awards: DetailAward[];
    theme?: DetailAwardsTheme;
    className?: string;
}) => {
    if (awards.length === 0) return null;

    return (
        <section className={className}>
            <div className="mx-auto w-full max-w-(--ap-page-width) max-md:px-6 md:px-[3rem]">
                <h2
                    className={cn(
                        'm-0',
                        theme === 'dark' ? 'text-white' : 'text-[#181818]',
                        SECTION_TITLE_TYPE,
                    )}
                >
                    Awards
                </h2>
                <DetailAwardsList
                    awards={awards}
                    theme={theme}
                    className="max-md:mt-6 md:mt-[3rem]"
                />
            </div>
        </section>
    );
};
